/**
 * Booking API Service
 * Handles all booking-related API calls
 */

export interface BookingResponse {
  id: string;
  sessionId: string;
  userId: string;
  status: BookingStatusType;
  totalAmount: number;
  storeCreditApplied?: number | null;
  refundMethod?: 'STORE_CREDIT' | 'STRIPE_REFUND' | null;
  stripeRefundAmount?: number | null;
  promotionCodeId?: string | null;
  promotionCodeSnapshot?: string | null;
  discountAmount?: number;
  memberPromotionCodeSnapshot?: string | null;
  memberDiscountAmount?: number;
  participants: Array<{
    id: string;
    childProfileId?: string | null;
    adultProfileId?: string | null;
    childProfile?: {
      id: string;
      name: string;
      preferredName: string;
      birthDate: string;
      notes?: string | null;
    } | null;
    adultProfile?: {
      id: string;
      name: string;
      phone: string;
    } | null;
  }>;
  session: {
    id: string;
    startTime: string;
    endTime: string;
    maxCapacity: number;
    currentBookings: number;
    activity: {
      id: string;
      titleCN: string | null;
      titleEN: string | null;
      price: number;
    };
  };
  createdAt: string;
  updatedAt: string;
}

export type BookingStatusType =
  | 'PENDING'
  | 'CONFIRMED'
  | 'CANCELLED'
  | 'REFUNDED'
  | 'CANCELLED_CREDIT'
  | 'CANCELLED_REFUND'
  | 'CANCELLED_PASS'
  | 'CANCELLED_BY_HOST'
  | 'TRANSFERRED'
  | 'pending'
  | 'confirmed'
  | 'cancelled'
  | 'refunded'
  | 'cancelled_credit'
  | 'cancelled_refund'
  | 'cancelled_pass'
  | 'cancelled_by_host'
  | 'transferred';

export interface TransferResponse {
  id: string;
  spotsReleased: number;
  status: string;
  transferCode: string | null;
}

export interface TransferCodeResponse {
  transferCode: string;
  shareUrl?: string;
}

export interface UserTransfer {
  id: string;
  bookingId: string;
  sessionId: string;
  spotsReleased: number;
  spotsFilled: number;
  status: string;
  transferCode: string | null;
  perSpotAmount: number;
  refundedAmount: number;
  passSessionsRestored: number;
  createdAt: string;
  booking: {
    totalAmount: number;
    session: {
      startTime: string;
      endTime: string;
      activity: {
        id: string;
        titleCN: string | null;
        titleEN: string | null;
        slug: string;
      };
    };
  };
  fillRecords: Array<{
    id: string;
    spotsFilled: number;
    refundAmount: number;
    createdAt: string;
  }>;
}

export interface CancelBookingResponse {
  booking: BookingResponse;
  storeCreditAmount: number;
  stripeRefundAmount: number;
}

export interface UserBookingsResponse {
  id: string;
  sessionId: string;
  userId: string;
  status: BookingStatusType;
  totalAmount: number;
  userPassId?: string | null;
  storeCreditApplied?: number | null;
  refundMethod?: 'STORE_CREDIT' | 'STRIPE_REFUND' | null;
  stripeRefundAmount?: number | null;
  participants: Array<{
    id: string;
    childProfileId?: string | null;
    adultProfileId?: string | null;
    childProfile?: {
      id: string;
      name: string;
      preferredName: string;
      birthDate: string;
      notes?: string | null;
    } | null;
    adultProfile?: {
      id: string;
      name: string;
      phone: string;
    } | null;
  }>;
  // Host-message availability for this booking's session (always present).
  // available flips true only after the detail/reminder email actually went out.
  hostMessage: { available: boolean };
  session: {
    id: string;
    startTime: string;
    endTime: string;
    maxCapacity: number;
    currentBookings: number;
    isCancelled: boolean;
    descriptionCN?: string | null;
    descriptionEN?: string | null;
    activity: {
      id: string;
      titleCN: string | null;
      titleEN: string | null;
      slug: string;
      price: number;
      childCountsTowardCapacity: boolean;
      adultCountsTowardCapacity: boolean;
      // host.userId matches Booking.userId when the viewer hosts this activity.
      host?: { userId: string };
      images: Array<{
        imageUrl: string;
        isCover: boolean;
      }>;
    };
  };
  createdAt: string;
  updatedAt: string;
}

// Host-published content (event detail + reminder) for one booking's session.
// Served only when the underlying email actually went out; all access denials
// come back as 404.
export interface BookingHostMessagesResponse {
  session: {
    id: string;
    startTime: string;
    endTime: string;
    activity: {
      id: string;
      slug: string;
      titleCN: string | null;
      titleEN: string | null;
      region: string[];
      locationDetails: string | null;
      location: string | null;
    };
  };
  messages: Array<{
    type: 'EVENT_DETAIL' | 'REMINDER';
    sentAt: string;
    message: string | null;
    detailLink?: string | null;
  }>;
  // Full email-template render of the sent content (detail email as the base,
  // reminder appended as a section); null when nothing is published yet.
  html: string | null;
}

class BookingApi {
  private apiUrl: string;

  constructor() {
    this.apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
  }

  /**
   * Get all bookings for the authenticated user
   * Requires authentication token
   */
  async getUserBookings(token: string): Promise<UserBookingsResponse[]> {
    const response = await fetch(`${this.apiUrl}/bookings/my-bookings`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch bookings');
    }

    const result = await response.json();
    return result.data || [];
  }

  /**
   * Get a specific booking by ID
   * Requires authentication token
   */
  async getBookingById(bookingId: string, token: string): Promise<BookingResponse> {
    const response = await fetch(`${this.apiUrl}/bookings/${bookingId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch booking');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Get host-published content (event detail + reminder) for a booking.
   * Throws Error('NOT_FOUND') on any 404 so the page can render its 404 state.
   */
  async getHostMessages(
    bookingId: string,
    token: string,
    lang?: string // The page renders in the site language it is viewed in
  ): Promise<BookingHostMessagesResponse> {
    const query = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    const response = await fetch(`${this.apiUrl}/bookings/${bookingId}/host-messages${query}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 404) {
      throw new Error('NOT_FOUND');
    }
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch host messages');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Cancel a booking
   * Requires authentication token
   */
  async cancelBooking(
    bookingId: string,
    token: string,
    refundMethod: 'STORE_CREDIT' | 'STRIPE_REFUND'
  ): Promise<CancelBookingResponse> {
    const response = await fetch(`${this.apiUrl}/bookings/${bookingId}/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ refundMethod }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to cancel booking');
    }

    const result = await response.json();
    return {
      booking: result.data,
      storeCreditAmount: result.storeCreditAmount || 0,
      stripeRefundAmount: result.stripeRefundAmount || 0,
    };
  }

  /**
   * Confirm a booking (admin/payment success)
   * Requires authentication token
   */
  async confirmBooking(bookingId: string, token: string): Promise<BookingResponse> {
    const response = await fetch(`${this.apiUrl}/bookings/${bookingId}/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to confirm booking');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Create a Stripe Checkout Session for payment
   * Returns a Stripe checkout URL (booking is only created after successful payment)
   */
  async createCheckoutSession(
    data: {
      sessionId: string;
      userId: string | null;
      guestInfo: {
        email: string;
        name: string;
      } | null;
      participants: {
        kids: Array<{
          profileId?: string; // Optional: for direct profile linking
          name: string;
          birthDate: string;
          notes?: string; // Combined notes including allergies, dietary restrictions, etc.
        }>;
        adults: Array<{
          profileId?: string; // Optional: for direct profile linking
          name: string;
          phone: string;
        }>;
      };
      photoVideoConsent: 'FULL' | 'BLURRED' | 'NO_PHOTO';
      utmSource?: string | null; // UTM source for attribution tracking
      transferCode?: string | null; // Transfer code for directed transfer matching
      promotionCode?: string; // Promotion code for discount
      promotionCodes?: string[]; // One platform promo code plus one member new user promo code
      waitlistId?: string | null; // Set when this booking originated from a waitlist claim
      locale?: string; // Site language; sets the Stripe page language and copy
    },
    token?: string
  ): Promise<{
    checkoutUrl: string | null;
    checkoutSessionId?: string;
    bookingId?: string;
    creditApplied?: number;
  }> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add auth token if available
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.apiUrl}/payments/create-checkout-session`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const err = new Error(errorData.error?.message || 'Failed to create checkout session');
      (err as Error & { code?: string }).code = errorData.error?.code;
      throw err;
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Initiate a transfer for a confirmed booking
   */
  async initiateTransfer(bookingId: string, token: string): Promise<TransferResponse> {
    const response = await fetch(`${this.apiUrl}/bookings/${bookingId}/transfer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to initiate transfer');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Generate a transfer code for sharing
   */
  async generateTransferCode(transferId: string, token: string): Promise<TransferCodeResponse> {
    const response = await fetch(`${this.apiUrl}/transfers/${transferId}/generate-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate transfer code');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Get user's transfers
   */
  async getUserTransfers(token: string): Promise<UserTransfer[]> {
    const response = await fetch(`${this.apiUrl}/transfers/my-transfers`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch transfers');
    }

    const result = await response.json();
    return result.data || [];
  }
}

export const bookingApi = new BookingApi();
